import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';

interface QueueStatus {
  waiting: number;
  active: number;
  completed: number;
  failed: number;
}

const API_BASE = window.location.origin;

async function fetchQueueStatus(): Promise<QueueStatus | null> {
  try {
    const adminToken = localStorage.getItem('admin_token');
    const res = await fetch(`${API_BASE}/api/queue/status`, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.success ? data.data : null;
  } catch {
    return null;
  }
}

async function clearQueue(): Promise<boolean> {
  try {
    const adminToken = localStorage.getItem('admin_token');
    const res = await fetch(`${API_BASE}/api/queue/clear`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${adminToken}`,
        'Content-Type': 'application/json',
      },
    });
    return res.ok;
  } catch {
    return false;
  }
}

function StatCard({
  label,
  value,
  color,
  icon,
}: {
  label: string;
  value: number;
  color: string;
  icon: string;
}) {
  return (
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.05)',
        border: `1px solid ${color}33`,
        borderRadius: '12px',
        padding: '24px',
        textAlign: 'center',
        transition: 'transform 0.2s, box-shadow 0.2s',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = `0 8px 24px ${color}22`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = '';
        (e.currentTarget as HTMLDivElement).style.boxShadow = '';
      }}
    >
      <div style={{ fontSize: '32px', marginBottom: '8px' }}>{icon}</div>
      <div
        style={{
          fontSize: '36px',
          fontWeight: 700,
          color,
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: '14px',
          color: 'rgba(255, 255, 255, 0.6)',
          marginTop: '4px',
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}
      >
        {label}
      </div>
    </div>
  );
}

export default function QueueDashboardPage() {
  const { isAuthenticated } = useAdmin();
  const navigate = useNavigate();
  const [status, setStatus] = useState<QueueStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }

    const loadStatus = async () => {
      const data = await fetchQueueStatus();
      setStatus(data);
      setLoading(false);
    };

    loadStatus();

    // Polling every 10 seconds
    const interval = setInterval(loadStatus, 10000);
    return () => clearInterval(interval);
  }, [isAuthenticated, navigate]);

  const handleClear = async () => {
    setClearing(true);
    setMessage(null);
    const success = await clearQueue();
    if (success) {
      setMessage('Очередь очищена');
      const data = await fetchQueueStatus();
      setStatus(data);
    } else {
      setMessage('Ошибка при очистке очереди');
    }
    setClearing(false);
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a3e 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#d4af37',
          fontSize: '18px',
        }}
      >
        Загрузка...
      </div>
    );
  }

  const total = status
    ? status.waiting + status.active + status.completed + status.failed
    : 0;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a3e 100%)',
        color: '#fff',
        fontFamily: "'Inter', sans-serif",
        padding: '40px 20px',
      }}
    >
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '40px',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <div>
            <h1
              style={{
                fontSize: '28px',
                fontWeight: 700,
                margin: 0,
                background: 'linear-gradient(135deg, #d4af37, #f4e4a0)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Job Queue Dashboard
            </h1>
            <p
              style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '14px',
                margin: '4px 0 0',
              }}
            >
              forecast-generation &middot; автообновление каждые 10 сек
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {message && (
              <span
                style={{
                  fontSize: '13px',
                  color: message.startsWith('Ошибка') ? '#ff6b6b' : '#4ade80',
                }}
              >
                {message}
              </span>
            )}
            <button
              onClick={handleClear}
              disabled={clearing}
              style={{
                background: clearing
                  ? 'rgba(255, 107, 107, 0.3)'
                  : 'rgba(255, 107, 107, 0.15)',
                border: '1px solid rgba(255, 107, 107, 0.4)',
                borderRadius: '8px',
                padding: '10px 20px',
                color: '#ff6b6b',
                cursor: clearing ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!clearing) {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'rgba(255, 107, 107, 0.25)';
                }
              }}
              onMouseLeave={(e) => {
                if (!clearing) {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'rgba(255, 107, 107, 0.15)';
                }
              }}
            >
              {clearing ? 'Очистка...' : 'Очистить очередь'}
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '20px',
            marginBottom: '32px',
          }}
        >
          <StatCard
            label="Ожидает"
            value={status?.waiting ?? 0}
            color="#fbbf24"
            icon="⏳"
          />
          <StatCard
            label="В работе"
            value={status?.active ?? 0}
            color="#60a5fa"
            icon="⚙️"
          />
          <StatCard
            label="Завершено"
            value={status?.completed ?? 0}
            color="#4ade80"
            icon="✅"
          />
          <StatCard
            label="Ошибки"
            value={status?.failed ?? 0}
            color="#ff6b6b"
            icon="❌"
          />
        </div>

        {/* Summary */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            borderRadius: '12px',
            padding: '24px',
          }}
        >
          <h3
            style={{
              fontSize: '16px',
              fontWeight: 600,
              margin: '0 0 16px',
              color: '#d4af37',
            }}
          >
            Сводка
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
            }}
          >
            <div>
              <span
                style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}
              >
                Всего задач
              </span>
              <div style={{ fontSize: '20px', fontWeight: 600, marginTop: '4px' }}>
                {total}
              </div>
            </div>
            <div>
              <span
                style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}
              >
                Загрузка
              </span>
              <div style={{ fontSize: '20px', fontWeight: 600, marginTop: '4px' }}>
                {status && status.active + status.waiting > 0
                  ? `${Math.round(
                      (status.active / Math.max(status.active + status.waiting, 1)) * 100
                    )}%`
                  : '0%'}
              </div>
            </div>
            <div>
              <span
                style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}
              >
                Успешность
              </span>
              <div style={{ fontSize: '20px', fontWeight: 600, marginTop: '4px' }}>
                {status && status.completed + status.failed > 0
                  ? `${Math.round(
                      (status.completed /
                        Math.max(status.completed + status.failed, 1)) *
                        100
                    )}%`
                  : '—'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
